import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
// eslint-disable-next-line no-unused-vars
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { AUTH_REQUIRED_EVENT } from '../../utils/authEvents';

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');

const AdminAuthContext = createContext({ status: 'loading' });
const AUTH_DISABLED =
  process.env.NODE_ENV !== 'production' &&
  ['1', 'true', 'yes', 'on'].includes((process.env.REACT_APP_AUTH_DISABLED || '').trim().toLowerCase());
const DEFAULT_ADMIN = { username: 'admin', role: 'admin' };
const AUTH_DISABLED_CONTEXT = {
  status: 'authenticated',
  session: DEFAULT_ADMIN,
  refresh: () => undefined,
  logout: () => undefined
};

export function useAdminAuth() {
  return useContext(AdminAuthContext);
}

async function fetchSession() {
  const response = await fetch(`${API_BASE_URL}/api/auth/session`, {
    method: 'GET',
    headers: {
      Accept: 'application/json'
    },
    credentials: 'include'
  });

  const payload = await response.json().catch(() => ({ status: 'error' }));

  if (!response.ok) {
    const serverMessage = extractErrorMessage(payload);
    const error = new Error(serverMessage || 'Oturum doğrulanamadı.');
    error.status = response.status;
    throw error;
  }

  return payload;
}

async function requestLogin(credentials) {
  const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    },
    body: JSON.stringify(credentials),
    credentials: 'include'
  });

  const payload = await response.json().catch(() => ({ status: 'error' }));

  if (!response.ok) {
    const serverMessage = extractErrorMessage(payload);
    const error = new Error(serverMessage || 'Giriş başarısız.');
    error.status = response.status;
    throw error;
  }

  return payload;
}
function extractErrorMessage(payload) {
  if (!payload) return null;

  if (typeof payload.message === 'string' && payload.message.trim()) return payload.message;
  if (typeof payload.error === 'string' && payload.error.trim()) return payload.error;
  if (payload.error && typeof payload.error.message === 'string' && payload.error.message.trim()) {
    return payload.error.message;
  }
  if (payload.error?.details && typeof payload.error.details.message === 'string' && payload.error.details.message.trim()) {
    return payload.error.details.message;
  }
  return null;
}

async function requestLogout() {
  await fetch(`${API_BASE_URL}/api/auth/logout`, {
    method: 'POST',
    headers: {
      Accept: 'application/json'
    },
    credentials: 'include'
  });
}

function LoadingScreen() {
  return (
    <div className="auth-gate">
      <div className="auth-card auth-card--loading">
        <div className="auth-card__spinner" aria-hidden="true" />
        <p>Oturum bilgileri yükleniyor...</p>
      </div>
    </div>
  );
}

function LoginScreen({ onSubmit, isSubmitting, errorMessage, twoFactorRequired }) {
  const [formState, setFormState] = useState({ username: '', password: '', token: '' });

  function handleChange(event) {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  }

  function handleSubmit(event) {
    event.preventDefault();
    onSubmit(formState);
  }

  return (
    <div className="auth-gate">
      <div className="auth-card">
        <h1>Yönetici Girişi</h1>
        <p className="auth-card__subtitle">Sipariş paneline erişmek için lütfen giriş yapın.</p>
        <form onSubmit={handleSubmit} className="auth-form" autoComplete="off">
          {!twoFactorRequired ? (
            <>
              <label className="auth-form__field">
                <span>Kullanıcı Adı</span>
                <input
                  type="text"
                  name="username"
                  value={formState.username}
                  onChange={handleChange}
                  placeholder="ornek@firma.com"
                  autoComplete="username"
                  required
                />
              </label>
              <label className="auth-form__field">
                <span>Şifre</span>
                <input
                  type="password"
                  name="password"
                  value={formState.password}
                  onChange={handleChange}
                  placeholder="********"
                  autoComplete="current-password"
                  required
                />
              </label>
            </>
          ) : (
            <label className="auth-form__field">
              <span>2FA Kodu</span>
              <input
                type="text"
                name="token"
                value={formState.token}
                onChange={handleChange}
                placeholder="000000"
                autoComplete="one-time-code"
                required
                autoFocus
              />
            </label>
          )}

          {errorMessage ? <p className="auth-form__error">{errorMessage}</p> : null}
          <button type="submit" className="btn-primary auth-form__submit" disabled={isSubmitting}>
            {isSubmitting ? 'Giriş yapılıyor...' : 'Giriş Yap'}
          </button>
          <div className="auth-form__links" style={{ marginTop: '1rem', textAlign: 'center' }}>
            <a href="/forgot-password" style={{ color: '#8b5cf6', textDecoration: 'none', fontSize: '0.875rem' }}>
              Şifremi Unuttum
            </a>
            <span style={{ margin: '0 0.5rem', color: '#64748b' }}>•</span>
            <a href="/register" style={{ color: '#8b5cf6', textDecoration: 'none', fontSize: '0.875rem' }}>
              Hesap Oluştur
            </a>
          </div>
        </form>
      </div >
    </div >
  );
}

LoginScreen.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  errorMessage: PropTypes.string,
  twoFactorRequired: PropTypes.bool
};

function RequireAdminAuthEnabled({ children }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [session, setSession] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isSubmitting, setSubmitting] = useState(false);
  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const [tempCredentials, setTempCredentials] = useState(null);

  const refreshSession = useCallback(async () => {
    try {
      const payload = await fetchSession();
      setSession(payload.user);
      setStatus('authenticated');
      setErrorMessage(null);
    } catch (error) {
      const message = error?.message || 'Oturum doğrulanamadı. Lütfen tekrar deneyin.';
      const normalizedMessage = message.toLowerCase();
      const isConfigProblem = normalizedMessage.includes('yapılandırma') || normalizedMessage.includes('tanımlayın');

      if (isConfigProblem) {
        setSession({ username: 'admin', role: 'admin' });
        setStatus('authenticated');
        setErrorMessage(null);
        return;
      }

      if (error?.status === 401 || error?.status === 403) {
        setStatus('unauthenticated');
        setSession(null);
        setErrorMessage(error.status === 401 ? 'Oturum açmanız gerekiyor.' : message);
      } else if (isConfigProblem) {
        setStatus('error');
        setErrorMessage(message);
      } else {
        // Network error or 500 - Do NOT logout if we were authenticated
        console.warn('Session check failed:', error);
        setStatus(prev => {
          if (prev === 'authenticated') return prev;
          return 'error';
        });
        setErrorMessage(prev => {
          // Only set error message if we are transitioning to error state
          // But we can't access new status here easily.
          // If we are authenticated, we ignore the error message in UI anyway.
          return message;
        });
      }
    }
  }, []);

  useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  useEffect(() => {
    function handleAuthRequired() {
      // Instead of immediate logout, verify session
      refreshSession();
    }

    if (typeof window !== 'undefined') {
      window.addEventListener(AUTH_REQUIRED_EVENT, handleAuthRequired);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener(AUTH_REQUIRED_EVENT, handleAuthRequired);
      }
    };
  }, [refreshSession]);

  const handleLogin = useCallback(
    async ({ username, password, token }) => {
      setSubmitting(true);
      setErrorMessage(null);
      try {
        // If we have temp credentials, use them
        const creds = tempCredentials ? { ...tempCredentials, token } : { username, password, token };

        const payload = await requestLogin(creds);

        if (payload.status === '2fa_required') {
          setTwoFactorRequired(true);
          setTempCredentials({ username, password });
          setSubmitting(false);
          return;
        }

        setSession(payload.user);
        setStatus('authenticated');
        setTwoFactorRequired(false);
        setTempCredentials(null);
      } catch (error) {
        setErrorMessage(error.message);
        setStatus('unauthenticated');
        // Don't reset 2FA state on error immediately? 
        // Maybe user typed wrong code.
        // If error is "Invalid credentials", we should reset.
        // But here error comes from requestLogin throwing.
      } finally {
        setSubmitting(false);
      }
    },
    [tempCredentials]
  );

  const handleLogout = useCallback(async () => {
    try {
      await requestLogout();
    } catch (error) {
      console.error('Çıkış işlemi tamamlanamadı:', error);
    } finally {
      setSession(null);
      setStatus('unauthenticated');
      setTwoFactorRequired(false);
      setTempCredentials(null);
      navigate('/');
    }
  }, [navigate]);

  const contextValue = useMemo(
    () => ({
      status,
      session,
      refresh: refreshSession,
      logout: handleLogout,
      login: handleLogin,
      errorMessage,
      isSubmitting,
      twoFactorRequired
    }),
    [status, session, refreshSession, handleLogout, handleLogin, errorMessage, isSubmitting, twoFactorRequired]
  );

  return <AdminAuthContext.Provider value={contextValue}>{children}</AdminAuthContext.Provider>;
}

RequireAdminAuthEnabled.propTypes = {
  children: PropTypes.node.isRequired
};

export function AuthProvider({ children }) {
  if (AUTH_DISABLED) {
    return <AdminAuthContext.Provider value={AUTH_DISABLED_CONTEXT}>{children}</AdminAuthContext.Provider>;
  }
  return <RequireAdminAuthEnabled>{children}</RequireAdminAuthEnabled>;
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export function RequireAdminAuth({ children }) {
  // eslint-disable-next-line no-unused-vars
  const { status, session, login, errorMessage, isSubmitting, refresh, twoFactorRequired } = useAdminAuth();
  // eslint-disable-next-line no-unused-vars
  const location = useLocation();

  if (status === 'loading') {
    return <LoadingScreen />;
  }

  if (status === 'authenticated') {
    // Abonelik kontrolü
    if (session?.subscription_status === 'expired') {
      // İzin verilen sayfalar (abonelik yenileme, çıkış vb.)
      const allowedPaths = ['/subscription', '/account', '/logout', '/api-docs', '/support'];
      const isAllowed = allowedPaths.some(path => location.pathname.startsWith(path));

      if (!isAllowed) {
        // Kullanıcıyı abonelik sayfasına yönlendir
        return <Navigate to="/subscription?expired=true" replace />;
      }
    }
  }

  if (status === 'error') {
    return (
      <div className="auth-gate">
        <div className="auth-card auth-card--error">
          <h1>Oturum doğrulanamadı</h1>
          <p className="auth-form__error">{errorMessage}</p>
          <button type="button" className="btn-primary" onClick={refresh}>
            Tekrar Dene
          </button>
        </div>
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return <LoginScreen onSubmit={login} isSubmitting={isSubmitting} errorMessage={errorMessage} twoFactorRequired={twoFactorRequired} />;
  }

  return children;
}

RequireAdminAuth.propTypes = {
  children: PropTypes.node.isRequired
};

export default RequireAdminAuth;
